// ULL — wire-formatet mellom ULL-broen og appen.
//
// ══ FORSKJELLEN FRA NJORD, OG HVORFOR DENNE FILA ER KORT ════════════════════
// NJORDs `api/typer.ts` måtte finne opp hele KOP-formatet lokalt, fordi det
// ikke fantes noen kontrakt. ULL er i motsatt situasjon: KONTRAKTEN FINNES
// ALLEREDE — `@skade/contracts/ugs.ts` eier sensoren, utløsningen, wire-
// konvolutten (`UgsMelding`), kanalnavnet (`UGS_KANAL`) og all analysen. Å skrive
// en «ULL-sensor» her ville vært nøyaktig den divergensen grunnloven §3 forbyr.
//
// Denne fila gjør derfor bare tre ting:
//   1. ADAPTERER kontraktens typer til det `@skade/sensorflate` trenger
//      (`Flatesensor`, `Flatekontakt`, `Flateobservasjon`).
//   2. Definerer de TRE tingene kontrakten ikke har, med begrunnelse: laget
//      (echelonen), ruta/aksen, og kommandoene ULL sender tilbake.
//   3. Oversetter UGS-domenet til flatens ordforråd (`ugsTilSensorrad`).
//
// ══ DET STORE MODELLVALGET: HVA ER EN «KONTAKT» I ULL? ══════════════════════
// I NJORD er en kontakt et fusjonert fartøy som broen leverer. ULL har ingen
// slik ting, og skal ikke ha det: en UGS-utløsning er et PUNKT i rom og tid, og
// SEKVENSEN (retning, fart, antall) regnes ut LOKALT av `lib/sekvens.ts` fra
// rekkefølgen. Sekvensen kan altså ikke komme fra broen — den er en tolkning,
// og tolkningen skal jegeren gjøre selv.
//
// Derfor er flatens «kontakt» i ULL = ÉN UTLØSNING (`Ullhendelse`). Det gir tre
// ting gratis fra lagermotoren, som alle er ekte behov:
//   * dedup på `utlosningId` og stabil rekkefølge,
//   * `flagg` — jegerens EGEN merking per utløsning («dette var en elg») som
//     broen aldri får overskrive,
//   * `sisteMaalingPerSensor`, som sensorstolpen bruker.
//
// ══ HVA SOM BØR FLYTTES INN I KONTRAKTEN ═══════════════════════════════════
// `Ulllag` og `Rute` hører i `contracts` (laget som echelon er SENSORGRAF §2.1;
// ruta er inndata til `dekningshull()`, som ALLEREDE er i kontrakten og som ikke
// har noe sted å hente ruta fra). De ligger lokalt fordi kontrakten ikke har dem
// ennå, og de ligger i ÉN fil så flyttingen blir et klipp-og-lim.
import type {
  UgsArt,
  UgsBatteri,
  UgsMalklasse,
  UgsSensor,
  UgsUtlosning,
  LatLon,
} from '@skade/contracts';
import type {
  Flatekontakt, Flateobservasjon, Flatesensor, MeldtTilstand, Ovelsemerket, Terskler,
} from '@skade/sensorflate';

// ── Sensorartene, som ord ───────────────────────────────────────────────────

/**
 * Navn på arten. `UgsArt` er kontraktens lukkede union — vi finner ikke opp en
 * egen. Denne tabellen er bare UI-tekst, og den er `Record<UgsArt, string>` med
 * vilje: legger kontrakten til en art, blir dette en byggefeil i stedet for en
 * tom celle i sensorstolpen.
 */
export const SENSORART_NAVN: Record<UgsArt, string> = {
  akustisk: 'Akustisk',
  seismisk: 'Seismisk',
  pir: 'PIR',
  kamera: 'Kamera',
  brytertråd: 'Brytertråd',
  magnetisk: 'Magnetisk',
};

/** Hva arten faktisk måler. Brukes i tomtilstander og i sekvenskortet. */
export const SENSORART_MAALER: Record<UgsArt, string> = {
  akustisk: 'lyd i luft (ingen peiling fra én mikrofon)',
  seismisk: 'rystelse i bakken (ingen retning)',
  pir: 'varmt objekt som krysser strålen',
  kamera: 'bilde ved utløsning',
  brytertråd: 'at tråden ble brutt på ett punkt',
  magnetisk: 'jern i bevegelse (ingen retning)',
};

/**
 * Hva arten IKKE kan si. Vises der en enkelt utløsning står alene, fordi det er
 * den vanligste feiltolkningen i hele appen: «sensoren utløste, altså kom det
 * derfra». En geofon vet ikke hvor skrittet gikk.
 */
export const SENSORART_KAN_IKKE: Record<UgsArt, string> = {
  akustisk: 'Én mikrofon peiler ikke. Nivået sier noe om avstand OG om kilden — de to kan ikke skilles.',
  seismisk: 'Rundstrålende av fysikk. En person 10 m nord gir samme utslag som en 10 m sør.',
  pir: 'Enkeltstråle sier «noe krysset». Bare dobbelstråle bærer retning, og da som fortegn på en akse.',
  kamera: 'Kameraet utløste. Hva som er på bildet vet ingen før et menneske har sett det.',
  brytertråd: 'Ingen signatur, altså ingen klasse. Men posisjonen er den skarpeste vi har.',
  magnetisk: 'Sier «jern i bevegelse» og ingenting mer — verken masse eller retning.',
};

// ── Sensoren, sett fra flata ────────────────────────────────────────────────

/**
 * Kontraktens `UgsSensor` oversatt til flatens minimumskrav.
 *
 * Hele `UgsSensor` bæres med i `ugs`, fordi geometri, batteri og posisjonsmetode
 * er DOMENE og skal vises som de er. Feltene på toppen er bare det
 * friskhetsmodellen i pakken regner på.
 */
export interface Sensorrad extends Flatesensor<UgsArt>, Ovelsemerket {
  /** Kontraktens sensor, urørt. `null` = vi kjenner den bare gjennom utløsninger. */
  ugs: UgsSensor | null;
}

/**
 * EN UGS ER IKKE EN RADAR: DEN MELDER IKKE PERIODISK.
 *
 * Dette er den viktigste domeneforskjellen mellom ULL og NJORD, og den ligger
 * her fordi den er lett å ta feil av. Friskhetsmodellen i `@skade/sensorflate`
 * svarer på «hvor gammel er MÅLINGEN», med 40 sekunder som taket for «stille».
 * Det er riktig for en radar som snurrer. For en geofon i en grøft er det
 * KATASTROFALT feil: en seismisk sensor som ikke har utløst på 40 sekunder er
 * ikke stille — det har bare ikke gått noen der. Det er hele hensikten med den.
 *
 * Livstegnet til en UGS er derfor `lenke.sistHortMs` (hjertebanken), ikke siste
 * utløsning. Og en batteridrevet node som beaconer hvert tiende sekund er tom
 * etter noen døgn, så hjertebanken kommer sjelden. Tersklene under er ULLs egne,
 * UVERIFISERTE, og bevisst romslige:
 *
 *   sen 30 min · stille 1 t · borte 3 t
 *
 * De skal måles mot den faktiske meshen før de brukes til noe som utløser
 * handling. Alderen på siste UTLØSNING vises for seg selv, aldri som «friskhet».
 */
export const ULL_TERSKLER: Terskler = { senMs: 1_800_000, stilleMs: 3_600_000, borteMs: 10_800_000 };

/**
 * UVERIFISERT terskel for lav spenning PER CELLE (volt).
 *
 * Bare brukbar når `celler` er kjent — 3,4 V betyr helt ulike ting for 1S og 2S,
 * og kontrakten sier det eksplisitt. 3,2 V/celle er knekket på en Li-ion-kurve;
 * under det går spenningen fort. For en prosentverdi advarer vi FØRST under 20 %,
 * og sier samtidig at prosenten kommer fra en flat utladningskurve.
 */
export const LAV_SPENNING_PER_CELLE_V = 3.2;
export const LAV_BATTERIPROSENT = 20;

/**
 * Batteriadvarsel i klartekst, eller `null` når det ikke finnes grunnlag for én.
 *
 * At funksjonen kan returnere `null` er poenget: en terminalspenning uten kjent
 * celletall kan IKKE vurderes, og en oppdiktet vurdering er verre enn ingen.
 */
export function batteriAdvarsel(b: UgsBatteri): string | null {
  if (b.enhet === 'prosent') {
    if (b.prosent >= LAV_BATTERIPROSENT) return null;
    return `Batteri ${b.prosent} % (${b.kilde}) — MERK at prosent fra en UGS oftest er en omregning av en FLAT utladningskurve; den kan bomme med dager`;
  }
  if (b.celler === undefined) {
    return `Batteri ${b.spenningV.toFixed(2)} V (${b.kilde}) — celletallet er ukjent, så spenningen kan ikke vurderes mot en terskel`;
  }
  const perCelle = b.spenningV / b.celler;
  if (perCelle >= LAV_SPENNING_PER_CELLE_V) return null;
  return `Batteri ${b.spenningV.toFixed(2)} V over ${b.celler} celle(r) = ${perCelle.toFixed(2)} V/celle, under ${LAV_SPENNING_PER_CELLE_V} V (UVERIFISERT) — noden kan falle snart`;
}

/** Batteriet som lesbar tekst, uten vurdering. */
export function batteriTekst(b: UgsBatteri): string {
  return b.enhet === 'volt'
    ? `${b.spenningV.toFixed(2)} V${b.celler !== undefined ? ` (${b.celler}S)` : ''} · ${b.kilde}`
    : `${b.prosent} % · ${b.kilde}`;
}

/**
 * `UgsSensor` → `Sensorrad`. Her bestemmes hva broens tilstand ER for en UGS.
 *
 * Rekkefølgen på reglene er rangert etter hva som gjør sensoren ubrukelig FØRST,
 * og hver gren bærer en merknad operatøren kan handle på:
 *
 *  * HENTET INN (`aktiv: false`) er `av` og ikke `feil`: det er et valg noen tok,
 *    og den dekker ingenting. `feil` her ville sendt laget ut for å lete etter en
 *    node som ligger i sekken.
 *  * `tapt` lenke er `feil`. For en UGS er dette dessuten et OPSEC-signal, og
 *    merknaden sier det: en node som blir stille midt i et område med aktivitet
 *    kan være FUNNET.
 *  * `ingen-lenke` er `degradert`, ikke `feil`. En ren loggende sensor stråler
 *    ikke — den kan altså ikke peiles inn, og det er et gyldig og godt valg for
 *    et jegerlag. Men den gir INGEN varsel i tid, og det skillet må stå i UI-et.
 */
export function ugsTilSensorrad(s: UgsSensor, ovelse = false): Sensorrad {
  const grunner: string[] = [];
  const batt = batteriAdvarsel(s.batteri);
  if (batt) grunner.push(batt);
  // Operatørens egen merknad fra utleggingen står SIST, men den står — den er
  // ofte det eneste som forklarer hvorfor noden oppfører seg som den gjør
  // («ligger i myr», «snudd av elg»).
  if (s.merknad) grunner.push(s.merknad);

  let tilstand: MeldtTilstand = 'ok';
  if (!s.aktiv) {
    tilstand = 'av';
    grunner.unshift(
      s.fjernetMs !== undefined
        ? `Hentet inn ${new Date(s.fjernetMs).toISOString()} — dekker ingenting`
        : 'Ikke aktiv — dekker ingenting',
    );
  } else if (s.lenke.kvalitet === 'tapt') {
    tilstand = 'feil';
    grunner.unshift(
      'Sambandslenken er TAPT. Vi forventet varsel og fikk det ikke. En UGS som blir stille midt i et område med aktivitet kan være funnet og fjernet',
    );
  } else if (s.lenke.kvalitet === 'ingen-lenke') {
    tilstand = 'degradert';
    grunner.unshift(
      'INGEN LENKE er et valg, ikke en feil: noden stråler ikke og kan derfor ikke peiles inn. Men den gir ingen varsel — utløsningene finnes bare i noden til den hentes',
    );
  } else if (s.lenke.kvalitet === 'marginal') {
    tilstand = 'degradert';
    grunner.unshift(
      `Marginal lenke${s.lenke.hopp !== undefined ? ` over ${s.lenke.hopp} hopp` : ''} — varselet kan komme for sent til å være handlingsbart`,
    );
  } else if (batt) {
    tilstand = 'degradert';
  }

  return {
    id: s.sensorId,
    art: s.art,
    navn: s.navn,
    tilstand,
    ...(grunner.length > 0 ? { merknad: grunner.join(' · ') } : {}),
    ugs: s,
    ...(ovelse ? { ovelse: true as const } : {}),
  };
}

/** Sensorens posisjon som `LatLon`, eller `null` når vi bare kjenner målingene. */
export function radPosisjon(r: Sensorrad): LatLon | null {
  return r.ugs ? { lat: r.ugs.lat, lon: r.ugs.lon } : null;
}

// ── Utløsningen, sett fra flata ─────────────────────────────────────────────

/**
 * Jegerens egen merking av en utløsning. `ingen` er den nøytrale verdien —
 * den ENESTE lagermotoren lar broen overskrive.
 *
 * `avskrevet-dyr` og `avskrevet-egen` er skilt fordi de betyr ulike ting neste
 * gang noen leser loggen: «det var en elg» er en observasjon om terrenget, mens
 * «det var vår egen patrulje» er en observasjon om egen bevegelse. Slått sammen
 * til «avskrevet» ville begge blitt borte.
 */
export type Ullflagg = 'ingen' | 'relevant' | 'avskrevet-dyr' | 'avskrevet-egen';

export const ULLFLAGG_NAVN: Record<Ullflagg, string> = {
  ingen: 'Ikke vurdert',
  relevant: 'Relevant',
  'avskrevet-dyr': 'Avskrevet — dyr',
  'avskrevet-egen': 'Avskrevet — egen styrke',
};

/**
 * Det lagermotoren trenger av en observasjon. Ingen målingsdata her: hele
 * utløsningen ligger på `Ullhendelse` (én utløsning = én hendelse), og å bære
 * den to steder ville gitt to sannheter å holde i sync.
 */
export type Ullobservasjon = Flateobservasjon;

/** ÉN utløsning, som flatens «kontakt». Se filens toppkommentar. */
export interface Ullhendelse extends Flatekontakt<Ullobservasjon, Ullflagg>, Ovelsemerket {
  sensorId: string;
  /** Kontraktens utløsning, urørt. Alt UI-et viser leses herfra. */
  utlosning: UgsUtlosning;
}

/**
 * `UgsUtlosning` → `Ullhendelse`.
 *
 * `tsMs` rundes til hele millisekunder fordi lagermotoren sammenligner
 * tidsstempler; `tidspunktMs` beholder desimalene sine inne i `utlosning`, der
 * sekvensanalysen trenger dem (en dobbelstråle-PIR skiller strålebrudd i titalls
 * millisekunder).
 */
export function utlosningTilHendelse(u: UgsUtlosning, ovelse = false): Ullhendelse {
  const ts = Math.round(u.tidspunktMs);
  return {
    id: u.utlosningId,
    sensorId: u.sensorId,
    sisteTsMs: ts,
    flagg: 'ingen',
    observasjoner: [{ id: u.utlosningId, sensorId: u.sensorId, kontaktId: u.utlosningId, tsMs: ts }],
    utlosning: u,
    ...(ovelse ? { ovelse: true as const } : {}),
  };
}

// ── Laget (echelonen) — LOKAL UTVIDELSE ─────────────────────────────────────

/**
 * Laget sensorene tilhører. SENSORGRAF §2.1 er bindende: echelon = LAG, og
 * aggregeringen skjer på organisasjon, aldri på geometri. `UgsSensor.lagId`
 * peker hit, men kontrakten har ingen type for selve laget — den bør inn i
 * `contracts` (sensorgraf.ts) fordi COP skal tegne samme echelon.
 *
 * `posisjon` er lagets EGEN plassering (skjulstedet), ikke sensorenes. Den er
 * valgfri, og det er ikke slurv: et jegerlag har en god grunn til å ikke skrive
 * sin egen posisjon inn i en app som kan bli tatt.
 */
export interface Ulllag extends Ovelsemerket {
  id: string;
  navn: string;
  /** Kallesignal — det som brukes over sambandet, og i `FRA` i en stridsmelding. */
  kallesignal?: string;
  lat?: number;
  lon?: number;
  /** SKADE-org for RBAC-scope, som `KopPost.distrikt` i NJORD. */
  distrikt?: string;
  omrade?: string;
}

// ── Ruta/aksen — LOKAL UTVIDELSE ────────────────────────────────────────────

/**
 * En rute eller akse `dekningshull()` skal analyseres langs.
 *
 * Kontrakten har analysen men ingen type for inndataen, så den ligger her.
 * `punkt` er knekkpunktene i den rekkefølgen man ville gått dem — retningen
 * betyr noe, fordi hullene rapporteres som «meter fra rutestart».
 *
 * `kilde` er med fordi det avgjør hvor mye analysen er verdt: en akse tegnet
 * etter kart er noe annet enn en sti man selv har gått med GPS.
 */
export interface Rute extends Ovelsemerket {
  id: string;
  navn: string;
  punkt: LatLon[];
  kilde: 'gått-med-gps' | 'tegnet-i-kart' | 'antatt';
  merknad?: string;
}

export const RUTEKILDE_NAVN: Record<Rute['kilde'], string> = {
  'gått-med-gps': 'Gått med GPS',
  'tegnet-i-kart': 'Tegnet i kart',
  antatt: 'ANTATT — ikke rekognosert',
};

// ── Wire-konvolutt ─────────────────────────────────────────────────────────

/**
 * Kanalnavnet er kontraktens (`UGS_KANAL`), ikke et nytt. Re-eksporteres bare
 * her så resten av appen har én importsti for wire-formatet.
 */
export { UGS_KANAL } from '@skade/contracts';

/**
 * Meldingene ULL-broen sender.
 *
 * De fem første er kontraktens `UgsMelding` ORDRETT — de valideres med
 * kontraktens eget zod-skjema i `api/kilde.ts`, ikke med håndskrevne vakter.
 * `lag` og `rute` er ULLs lokale utvidelser (se over), og de er de eneste som
 * må valideres for hånd.
 */
/**
 * `ovelse` står på KONVOLUTTEN fordi det er konvolutten øvingskilden stempler
 * (`lagOvelseskilde` — den ene utgangen ut av kilden). `lib/lager.ts` bærer
 * stempelet videre ned på sensorraden, hendelsen og ruta.
 *
 * En EKTE bro kan ikke sette flagget: `tolkMelding` i `api/kilde.ts` parser med
 * kontraktens skjema og de to lokale vaktene, og ingen av dem leser `ovelse` fra
 * wire. Flagget kan altså bare oppstå på klienten, av øvingskilden.
 */
export type UllMelding = Ovelsemerket & (
  | { type: 'sensor'; sensor: UgsSensor }
  | { type: 'utlosning'; utlosning: UgsUtlosning }
  | { type: 'status'; sensorId: string; batteri?: UgsBatteri; lenke?: UgsSensor['lenke']; tsMs: number }
  | { type: 'borte'; sensorId: string; arsak: string }
  | { type: 'fjernet'; sensorId: string; tsMs: number; av?: string }
  | { type: 'lag'; lag: Ulllag }
  | { type: 'rute'; rute: Rute }
);

/**
 * Det ULL sender TILBAKE. Alt her har en konsekvens i verden, og to av dem
 * STRÅLER — se EMCON-bryteren i `main.ts`.
 *
 * `vurderBilde` er den viktigste: den løfter en kamerautløsning fra «kameraet
 * utløste» til «et menneske har sett hva det var», og det er den eneste
 * UGS-verdien som fortjener høy konfidens (`KONFIDENS_VURDERT_BILDE` i ugs.ts).
 * Den sendes til broen fordi vurderingen skal bli en del av utløsningen for ALLE
 * som leser den, ikke bare for denne skjermen.
 */
export type UllKommando =
  | { type: 'vurderBilde'; utlosningId: string; vurdering: UgsMalklasse; vurdertAv: string }
  /** Ingen av de mulige klassene passer — bildet viser ingenting brukbart. */
  | { type: 'avvisBilde'; utlosningId: string; vurdertAv: string; begrunnelse: string }
  | { type: 'settFlagg'; utlosningId: string; flagg: Ullflagg }
  /** Meldingen inn til SKADE. `felter` følger `REPORT_FIELDS.MELDING`. */
  | {
      type: 'melding';
      meldingId: string;
      meldingstype: 'MELDING';
      felter: Record<string, string>;
      lat?: number;
      lon?: number;
      tsMs: number;
      /** Klartekstformen som kan leses opp over samband. Sendes med så SKADE og
       *  jegeren har PRESIS samme ordlyd — en omformatering i mottak er en
       *  divergens ingen oppdager før to folk leser ulikt over radio. */
      klartekst: string;
    };
